import { createAsyncThunk } from '@reduxjs/toolkit'
import { SOMETHING_WHEN_WRONG } from 'constant/errorMessages'
import { callErrorMsg } from 'helpers/errorMsg'
import {
  CreateRouteWavePayloadType,
  CreateRouteWaveResponseType,
  GetVendorResponseType,
  MAX_SO_CREATED_TIME_SENTINEL,
  RouteMasterLogType,
  RouteWaveConstantsType,
  RouteWaveDetailType,
  UpdateRouteWaveDestinationPayloadType,
  UpdateRouteWavePayloadType,
  VendorQueryType,
  createRouteWave,
  getRouteMasterDetail,
  getRouteWaveConstants,
  getRouteWaveDetail,
  getVendor,
  updateRouteWave,
} from 'features/Midmile/service/routeAndSchedule'

export const SLICE_NAME = 'routeAndScheduleAddEdit'

export const fetchRouteConfig = createAsyncThunk<RouteWaveConstantsType>(
  `${SLICE_NAME}/fetchRouteConfig`,
  async (_, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await getRouteWaveConstants()

      return data
    } catch (error) {
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchRouteMasterDetail = createAsyncThunk<
  RouteWaveDetailType,
  string | number,
  RejectValueType
>(`${SLICE_NAME}/fetchRouteMasterDetail`, async (id, { rejectWithValue }) => {
  try {
    const {
      data: { data },
    } = await getRouteWaveDetail(id)

    return data
  } catch (error) {
    callErrorMsg(error)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

export const fetchRouteMasterLogs = createAsyncThunk<RouteMasterLogType[], string | number>(
  `${SLICE_NAME}/fetchRouteMasterLogs`,
  async (id) => {
    try {
      const {
        data: { data },
      } = await getRouteMasterDetail(id)

      return data.logs || []
    } catch {
      return []
    }
  },
)

export const addRouteMaster = createAsyncThunk<
  CreateRouteWaveResponseType,
  undefined,
  RejectValueType
>(`${SLICE_NAME}/addRouteMaster`, async (_, { rejectWithValue, getState }) => {
  const {
    routeAndScheduleAddEdit: { formData, origin, destinations },
  } = getState() as StoreStateType

  if (!origin.locationID) {
    callErrorMsg('Lokasi asal wajib diisi')
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }

  const payload: CreateRouteWavePayloadType = {
    name: formData.name,
    rit: Number(formData.rit) || 0,
    wave: Number(formData.wave) || 0,
    location_id: origin.locationID,
    distance_type: formData.distanceType,
    trip_type: formData.tripType,
    departure_time: formData.departureTime ? `${formData.departureTime} +0700` : '',
    max_so_created_time: MAX_SO_CREATED_TIME_SENTINEL,
    destinations: destinations
      .filter((d) => d.locationID !== null)
      .map((d) => ({
        location_id: d.locationID as number,
        estimated_arrival_time: d.estimatedArrivalTime ? `${d.estimatedArrivalTime} +0700` : '',
      })),
  }

  try {
    const { data } = await createRouteWave(payload)

    return data
  } catch (error) {
    callErrorMsg(error)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

export const updateRouteMaster = createAsyncThunk<void, number | string, RejectValueType>(
  `${SLICE_NAME}/updateRouteMaster`,
  async (id, { rejectWithValue, getState }) => {
    const {
      routeAndScheduleAddEdit: { formData, origin, destinations, deletedDestinationIds },
    } = getState() as StoreStateType

    const updateDestinations: UpdateRouteWaveDestinationPayloadType[] = destinations
      .filter((d) => d.locationID !== null)
      .map((d, idx) => ({
        id: d.status === 'UPDATE' ? d.remoteId : 0,
        location_id: d.locationID as number,
        sequence: idx + 1,
        estimated_arrival_time: d.estimatedArrivalTime ? `${d.estimatedArrivalTime} +0700` : '',
      }))

    const payload: UpdateRouteWavePayloadType = {
      active: formData.active,
      name: formData.name,
      rit: Number(formData.rit) || 0,
      wave: Number(formData.wave) || 0,
      location_id: origin.locationID || undefined,
      distance_type: formData.distanceType,
      trip_type: formData.tripType,
      departure_time: formData.departureTime ? `${formData.departureTime} +0700` : '',
      max_so_created_time: MAX_SO_CREATED_TIME_SENTINEL,
      delete_route_wave_destination_ids: deletedDestinationIds,
      update_route_wave_destinations: updateDestinations,
    }

    try {
      await updateRouteWave(id, payload)
      return undefined
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchVendor = createAsyncThunk<GetVendorResponseType['data'], VendorQueryType>(
  `${SLICE_NAME}/fetchVendor`,
  async (params, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await getVendor(params)

      return data
    } catch (error) {
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)
